import React, { type JSX } from 'react';
import { FormField, SpaceBetween, Select } from '@amzn/awsui-components-core';
import {
  FeedbackStrategy,
  FeedbackFormState,
  FeedbackFormHandlers,
  FeedbackSubmissionContext,
} from './FeedbackStrategy';
import { PublicationFeedbackTypeOptions } from '../../utils/SelectableOptions';
import RichTextDescriptionField from './RichTextDescriptionField';
import { getText } from '@amzn/awscat-editor';

const FeedbackTextMinLength = 50;
const FeedbackTextMaxLength = 1500;

/**
 * Strategy for handling improvement feedback on published documents.
 *
 * Intent:
 * Enables users to report issues, suggest improvements, or request updates to
 * published documentation. This is the most comprehensive feedback type with
 * detailed fields to help authors understand and address the feedback.
 *
 * Characteristics:
 * - User selects issue type from predefined options (accuracy, clarity, etc.)
 * - Required "improvement" field explaining what needs to change
 * - Optional "intended action" field for context on user's use case
 * - Sent directly to document authors for action
 *
 * Validation:
 * - Requires issue type selection
 * - Requires improvement text between 50-1500 characters
 * - Intended action field is optional
 *
 * Submission:
 * - Sends publication feedback with selected issue type
 * - Includes improvement text, intended action, and any comments
 * - Routes to author for review and potential content updates
 */
export class DocumentFeedbackStrategy implements FeedbackStrategy {
  getHeaderText(): string {
    return 'Report feedback to author';
  }

  getSubjectLabel(): string {
    return 'Document title';
  }

  getDateLabel(): string {
    return 'Publish date';
  }

  renderFormFields(state: FeedbackFormState, handlers: FeedbackFormHandlers): JSX.Element {
    return (
      <SpaceBetween size="m">
        <FormField
          label="Type of feedback"
          errorText={!state.isFormValid && !state.selectedIssue && 'Required field'}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
          stretch
        >
          <Select
            selectedOption={state.selectedIssue}
            onChange={({ detail }) => handlers.setSelectedIssue(detail.selectedOption)}
            options={PublicationFeedbackTypeOptions}
            selectedAriaLabel="Choose a type"
            ariaRequired
          />
        </FormField>
        <RichTextDescriptionField
          label={'Description'}
          value={state.improvementFeedbackText}
          onChange={handlers.setImprovementFeedbackText}
          minLength={FeedbackTextMinLength}
          maxLength={FeedbackTextMaxLength}
          onValidationChange={handlers.onDescriptionValidationChange}
        />
      </SpaceBetween>
    );
  }

  validate(state: FeedbackFormState): boolean {
    const plainText = getText(state.improvementFeedbackText);
    const length = plainText.length;
    return (
      !!state.selectedIssue && length >= FeedbackTextMinLength && length <= FeedbackTextMaxLength
    );
  }

  async submit(state: FeedbackFormState, context: FeedbackSubmissionContext): Promise<void> {
    await context.mplService.sendPublicationFeedback(
      context.contentId,
      state.selectedIssue?.value,
      state.improvementFeedbackText,
      state.actionFeedbackText,
      state.comment,
    );
  }
}
